/**
 * A text redline whose paragraphs sit both in table cells and in the body, split into the
 * parts the applier writes each in one piece.
 *
 * WHY
 *   The applier picks the cell writer or the package by the first paragraph of the card's
 *   range (`ProposalApplier.applyOnBookmarks`). A card whose range opens on a body paragraph
 *   and crosses a table went in as one package over body and table alike, and Word wrote a
 *   cell's change into the body paragraph standing before the table: #2279 QA section 10,
 *   change_throughout #19, "B. Fees" came back as "Vendor" while the Party cell kept its words
 *   (`.claude/cell-writer-doors-audit.md` §1, 10-20). Every caller of the applier (a card's
 *   Apply, Apply All's cards for the applier, a live-review remedy) writes through here.
 *
 * CASES
 *   - every paragraph in a cell, or none -> no parts: the card is written whole, as it always was
 *   - both                               -> one part for the cells, which the cell writer writes
 *                                           cell by cell, and one part per body paragraph, each
 *                                           on its own range, so no write's range crosses a table
 */
import { NO_TABLE_CHANGES } from "@common/utils/redline/table-operations";
import type { TextsRedlinePayload } from "@common/utils/redline/wire/redline-payload";

import { withWordContextRead } from "../../document";

/** One write of a split redline: the bookmarks it is written over and what it writes there. */
export interface RedlinePartAcrossATable {
    bookmarkNames: string[];
    redline: TextsRedlinePayload;
}

/**
 * The redline's bookmarks whose paragraph Word puts in a table cell.
 *
 * NULL-OBJECT
 *   A bookmark Word no longer holds is no cell: the applier's recovery looks for it as it
 *   would for any card.
 */
async function bookmarksInTableCellsOf(bookmarkNames: string[]): Promise<Set<string>> {
    return withWordContextRead(async (context) => {
        const bookmarkRanges = bookmarkNames.map((bookmarkName) => {
            const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
            bookmarkRange.load("isNullObject");
            return { bookmarkName, bookmarkRange };
        });
        await context.sync();
        const cellsOfHeldBookmarks = bookmarkRanges
            .filter(({ bookmarkRange }) => !bookmarkRange.isNullObject)
            .map(({ bookmarkName, bookmarkRange }) => {
                const tableCellOfBookmarkParagraph = bookmarkRange.paragraphs.getFirst().parentTableCellOrNullObject;
                tableCellOfBookmarkParagraph.load("isNullObject");
                return { bookmarkName, tableCellOfBookmarkParagraph };
            });
        await context.sync();
        return new Set(cellsOfHeldBookmarks.filter(({ tableCellOfBookmarkParagraph }) => !tableCellOfBookmarkParagraph.isNullObject).map(({ bookmarkName }) => bookmarkName));
    });
}

/**
 * The part of the redline that writes the paragraphs `paragraphBookmarks` names, with the
 * new paragraphs that follow them and the reverts on them.
 *
 * CASES, for the redline's row and table changes
 *   - the part of the cells -> carries them: they are the table's
 *   - a body paragraph      -> none
 */
function partOf(redline: TextsRedlinePayload, paragraphBookmarks: ReadonlySet<string>, carriesTableChanges: boolean): RedlinePartAcrossATable {
    return {
        bookmarkNames: Array.from(paragraphBookmarks),
        redline: {
            ...redline,
            paragraphs: redline.paragraphs.filter((cardParagraph) => cardParagraph.bookmark !== null && paragraphBookmarks.has(cardParagraph.bookmark)),
            insertions: redline.insertions.filter((insertion) => paragraphBookmarks.has(insertion.afterBookmark)),
            reverts: redline.reverts.filter((revert) => paragraphBookmarks.has(revert.bookmark)),
            /** CASES: the part of the cells keeps the table's changes; a body paragraph's part carries none. */
            tableChanges: carriesTableChanges ? redline.tableChanges : NO_TABLE_CHANGES,
        },
    };
}

/**
 * Whether every piece of the redline can be given to one part: each paragraph names one of
 * the bookmarks written over, each new paragraph follows one, and each revert sits on one.
 *
 * WHY
 *   A paragraph with no bookmark, a new paragraph chained after another new one, or a revert
 *   on a paragraph the card does not name has no part of its own to go in; split, it would be
 *   written by no part.
 */
function everyPieceBelongsToAPart(redline: TextsRedlinePayload, bookmarkNames: string[]): boolean {
    const namedBookmarks = new Set(bookmarkNames);
    return redline.paragraphs.every((cardParagraph) => cardParagraph.bookmark !== null && namedBookmarks.has(cardParagraph.bookmark))
        && redline.insertions.every((insertion) => namedBookmarks.has(insertion.afterBookmark))
        && redline.reverts.every((revert) => namedBookmarks.has(revert.bookmark));
}

/**
 * The parts to write a text redline in, or null when it is written whole.
 *
 * NULL
 *   Every paragraph of the redline is in a cell, or none is: one write carries it, as before.
 *   Also a redline one of whose pieces belongs to no part (`everyPieceBelongsToAPart`): written
 *   whole, as before, rather than losing the piece.
 *
 * EXAMPLE
 *   #19 over "A. Supplier Services" (body), "Customer may request…" (body), the Party cell of
 *   row 2 and the Role cell of row 3
 *   -> [cells: Party row 2, Role row 3] [body: "A. Supplier Services"] [body: "Customer may request…"]
 */
export async function partsOfARedlineAcrossATable(redline: TextsRedlinePayload, bookmarkNames: string[]): Promise<RedlinePartAcrossATable[] | null> {
    /** NULL: one bookmark is one paragraph, in a cell or not, and Word is not asked. */
    if (bookmarkNames.length < 2) return null;
    /** NULL: a piece of the redline would belong to no part. */
    if (!everyPieceBelongsToAPart(redline, bookmarkNames)) return null;
    const cellBookmarks = await bookmarksInTableCellsOf(bookmarkNames);
    const bodyBookmarks = bookmarkNames.filter((bookmarkName) => !cellBookmarks.has(bookmarkName));
    /** NULL: all cells or all body, written whole. */
    if (cellBookmarks.size === 0 || bodyBookmarks.length === 0) return null;
    return [
        partOf(redline, cellBookmarks, true),
        ...bodyBookmarks.map((bodyBookmark) => partOf(redline, new Set([bodyBookmark]), false)),
    ];
}
